import { useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, InlineStack, ProgressBar, Spinner, Text } from '@shopify/polaris';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_FAILURES = 3;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/*
  Copies the inquiry files the user picked in "Make purchase order" onto the new order.
  While the order is still unsaved it only announces the copy; once the order has an id it starts the copy on the
  server (POST /:id/copy-inquiry-documents) and polls the same job endpoint as a purchase duplicate.
*/
export default function InquiryDocumentCopyBanner({ purchaseId, request, onFinished, onDismiss }) {
  const [job, setJob] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const startedRef = useRef(false);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!request || !purchaseId || purchaseId === 'new' || startedRef.current) return;
    startedRef.current = true;

    const poll = async (jobId) => {
      let failures = 0;
      while (!unmountedRef.current) {
        await sleep(POLL_INTERVAL_MS);
        try {
          const response = await fetch(`/api/purchases/duplicate-jobs/${jobId}`);
          const snapshot = await response.json();
          if (!response.ok) throw new Error(snapshot?.msg || 'Could not read the file copy progress.');
          if (unmountedRef.current) return;
          failures = 0;
          setJob(snapshot);
          if (snapshot.status === 'done') return onFinished?.(snapshot);
        } catch (error) {
          failures += 1;
          if (failures >= MAX_POLL_FAILURES) {
            if (!unmountedRef.current) setErrorMessage(`${error.message} The server keeps copying; the files appear on this order once finished.`);
            return;
          }
        }
      }
    };

    const start = async () => {
      try {
        const response = await fetch(`/api/purchases/${purchaseId}/copy-inquiry-documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inquiryId: request.inquiryId, documentIds: request.documentIds })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.msg || 'The inquiry files could not be copied.');
        setJob(data.fileCopy);
        if (data.fileCopy.status === 'done') return onFinished?.(data.fileCopy);
        poll(data.fileCopy.jobId);
      } catch (error) {
        setErrorMessage(error.message || 'The inquiry files could not be copied.');
      }
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId, request]);

  if (!request) return null;
  const count = request.documentIds.length;

  if (purchaseId === 'new') {
    return (
      <Banner tone="info" title={`${count} file(s) from the inquiry will be copied`}>
        <p>They are copied to this purchase order as soon as it is saved.</p>
      </Banner>
    );
  }

  if (errorMessage) {
    return (
      <Banner tone="warning" title="Inquiry files" onDismiss={onDismiss}>
        <p>{errorMessage}</p>
      </Banner>
    );
  }

  const total = job?.total || count;
  const processed = Math.min(total, job?.processed || 0);
  const warnings = Array.isArray(job?.warnings) ? job.warnings : [];

  if (job?.status === 'done') {
    return (
      <Banner tone={warnings.length ? 'warning' : 'success'} title={`${job.copied?.length || 0} of ${total} inquiry file(s) copied`} onDismiss={onDismiss}>
        {warnings.length ? (
          <BlockStack gap="100">
            {warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </BlockStack>
        ) : null}
      </Banner>
    );
  }

  return (
    <Banner tone="info" title="Copying the inquiry files">
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center">
          <Spinner size="small" />
          <Text as="span">
            {processed} of {total}
            {job?.currentFilename ? ` · ${job.currentFilename}` : ''}
          </Text>
        </InlineStack>
        <ProgressBar progress={total ? Math.round((processed / total) * 100) : 0} size="small" />
      </BlockStack>
    </Banner>
  );
}
